// src/components/PrivateRoute.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import Loading from "./Loading";

type Props = { children: React.ReactNode };

const AUDIENCE = "https://sales.ourmars.net/api";
const SCOPE =
  "openid profile email offline_access read:estimates write:estimates profile:read profile:write";

export default function PrivateRoute({ children }: Props) {
  const {
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
    loginWithRedirect,
  } = useAuth0();

  const isEmbedded = useMemo(
    () => typeof window !== "undefined" && window.self !== window.top,
    []
  );

  const [ready, setReady] = useState(false);
  const [needLogin, setNeedLogin] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (isLoading) return;

    // 이미 인증이면 통과
    if (isAuthenticated) {
      setReady(true);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        // 1회만 silent 시도 (RT가 있으면 성공)
        await getAccessTokenSilently({
          authorizationParams: { audience: AUDIENCE, scope: SCOPE },
        });
        if (!cancelled) setReady(true);
      } catch (e: any) {
        const code = String(e?.error || e?.message || "");
        // 임베드(Confluence iframe)에서는 팝업/리다이렉트가 막혀서
        // 로그인 필요/RT 없음이면 "새 탭 로그인"으로 유도
        if (
          isEmbedded &&
          (/missing[_ ]?refresh[_ ]?token/i.test(code) ||
            /login_required/i.test(code) ||
            /consent_required/i.test(code))
        ) {
          if (!cancelled) setNeedLogin(true);
          return;
        }

        // 탑레벨이면 정상 리다이렉트
        if (!isEmbedded) {
          await loginWithRedirect({
            appState: {
              returnTo: window.location.pathname + window.location.search,
            },
            authorizationParams: {
              audience: AUDIENCE,
              scope: SCOPE,
              prompt: "login",
            },
          });
          return;
        }

        if (!cancelled) setErr(code);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isLoading,
    isAuthenticated,
    getAccessTokenSilently,
    loginWithRedirect,
    isEmbedded,
  ]);

  if (isLoading) return <Loading />;
  if (ready || isAuthenticated) return <>{children}</>;

  if (needLogin) {
    // ⚠️ iframe sandbox에서는 팝업/리다이렉트가 막히므로 "새 탭" 링크만 제공
    return (
      <div style={{ padding: 16, fontFamily: "system-ui" }}>
        <p>이 콘텐츠를 보려면 한 번 로그인/동의가 필요합니다.</p>
        <a
          href="https://sales.ourmars.net/?login=1"
          target="_blank"
          rel="noopener noreferrer"
        >
          새 탭에서 로그인 열기
        </a>
        <p style={{ marginTop: 8 }}>로그인 완료 후 이 페이지를 새로고침하세요.</p>
      </div>
    );
  }

  if (err) return <div style={{ padding: 16 }}>오류: {err}</div>;
  return null;
}
